import { memo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, FileText, Phone, Info, Package, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { HighlightedHeading } from '@/components/ui/highlighted-heading';

interface LinkItem {
  title: string;
  description: string;
  url: string;
  icon: any;
  iconSrc?: string;
}

interface InternalLinksSectionProps {
  currentPage: string;
  variant?: 'default' | 'compact';
}

const allLinks: Record<string, LinkItem> = {
  evenements: {
    title: "Évènements Particuliers",
    description: "Mariages, anniversaires signature, demandes en mariage et célébrations privées sur-mesure",
    url: "/univers-particuliers",
    icon: Users
  },
  professionnels: {
    title: "Évènements Professionnels",
    description: "Séminaires stratégiques, lancements de produits et galas corporate",
    url: "/evenement-entreprise-prestige",
    icon: Briefcase
  },
  catalogue: {
    title: "Notre prospectus",
    description: "Recevez notre prospectus par email : prestations, lieux d'exception et savoir-faire",
    url: "/prospectus-particuliers",
    icon: Package
  },
  devis: {
    title: "Demander un Devis",
    description: "Obtenez votre devis personnalisé en quelques minutes",
    url: "/devis",
    icon: FileText
  },
  demande_pro: {
    title: "Demande Professionnelle",
    description: "Créez votre demande d'évènement professionnel sur-mesure",
    url: "/nouvelle-demande-evenement",
    icon: FileText
  },
  contact: {
    title: "Nous Contacter",
    description: "Échangeons sur votre projet, réponse sous 24h ouvrées",
    url: "/contact",
    icon: Phone
  },
  about: {
    title: "À Propos",
    description: "Découvrez l'histoire et les valeurs de Maison Eridia",
    url: "/a-propos",
    icon: Info
  }
};

const getRelevantLinks = (currentPage: string): LinkItem[] => {
  const linkPriority: Record<string, string[]> = {
    '/': ['evenements', 'professionnels', 'devis'],
    '/evenements': ['devis', 'contact', 'about'],
    '/evenements/:slug/personnaliser': ['evenements', 'devis', 'contact'],
    '/evenement-entreprise-prestige': ['demande_pro', 'contact', 'catalogue'],
    '/prospectus-particuliers': ['evenements', 'professionnels', 'demande_pro'],
    '/prospectus-professionnels': ['evenements', 'professionnels', 'demande_pro'],
    '/devis': ['evenements', 'professionnels', 'contact'],
    '/contact': ['devis', 'evenements', 'about'],
    '/a-propos': ['evenements', 'professionnels', 'contact']
  };

  // Normaliser le currentPage pour les routes dynamiques
  let normalizedPage = currentPage;
  if (currentPage.includes('/evenements/') && currentPage.includes('/personnaliser')) {
    normalizedPage = '/evenements/:slug/personnaliser';
  }

  const priority = linkPriority[normalizedPage] || ['evenements', 'professionnels', 'devis'];
  
  return priority.map(key => allLinks[key]).filter(Boolean);
};

export const InternalLinksSection = memo(({ currentPage, variant = 'default' }: InternalLinksSectionProps) => {
  const links = getRelevantLinks(currentPage);

  if (variant === 'compact') {
    return (
      <section className="relative bg-[#f8f9fa] py-12 overflow-hidden">
        {/* Déco radiale subtile */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(243,154,162,0.03),transparent_50%)] pointer-events-none" />
        
        <div className="container mx-auto px-4 max-w-6xl relative">
          <HighlightedHeading
            highlightedText="aussi"
            className="text-2xl md:text-3xl mb-8"
          >
            Découvrez aussi
          </HighlightedHeading>
          <div className="grid md:grid-cols-3 gap-6">
            {links.map((link, index) => (
              <Link 
                key={index} 
                to={link.url}
                className="group"
              >
                <Card className="h-full hover:shadow-[0_16px_36px_rgba(10,37,64,0.12)] transition-all duration-300 bg-white border border-[#0a2540]/10 hover:border-[#f39aa2]/50 rounded-[18px] hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-gradient-to-b from-[#ffeef0] to-[#fde5e7] rounded-full flex items-center justify-center group-hover:scale-110 transition-all shadow-[inset_0_0_0_1px_rgba(243,154,162,0.35)]">
                          <link.icon className="w-6 h-6 text-[#f39aa2]" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-[#0a2540] mb-2 group-hover:text-[#f39aa2] transition-colors">
                          {link.title}
                        </h3>
                        <p className="text-sm text-[#55626f] mb-3">
                          {link.description}
                        </p>
                        <div className="flex items-center text-[#f39aa2] text-sm font-bold group-hover:translate-x-2 transition-transform">
                          En savoir plus
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative bg-white py-16 overflow-hidden">
      {/* Déco radiale subtile */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(243,154,162,0.03),transparent_50%)] pointer-events-none" />
      
      <div className="container mx-auto px-4 max-w-6xl relative">
        <div className="text-center mb-12">
          <HighlightedHeading
            highlightedText="aussi"
            className="text-3xl md:text-4xl mb-4"
          >
            Vous aimerez aussi
          </HighlightedHeading>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {links.map((link, index) => (
            <Link 
              key={index} 
              to={link.url}
              className="group"
            >
              <Card className="h-full hover:shadow-[0_16px_36px_rgba(10,37,64,0.12)] transition-all duration-300 bg-white border border-[#0a2540]/10 hover:border-[#f39aa2]/50 rounded-[18px] hover:-translate-y-1">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-gradient-to-b from-[#ffeef0] to-[#fde5e7] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-all duration-300 shadow-[inset_0_0_0_1px_rgba(243,154,162,0.35)]">
                    <link.icon className="w-8 h-8 text-[#f39aa2] transition-colors" />
                  </div>
                  <h3 className="font-playfair text-xl font-bold text-[#0a2540] mb-4 group-hover:text-[#f39aa2] transition-colors">
                    {link.title}
                  </h3>
                  <p className="text-[#55626f] mb-6 leading-relaxed">
                    {link.description}
                  </p>
                  <div className="flex items-center justify-center text-[#f39aa2] font-bold group-hover:translate-x-2 transition-transform">
                    Découvrir
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
});

InternalLinksSection.displayName = 'InternalLinksSection';
